import {
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  LoadingOverlay,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconWindow } from "@tabler/icons-react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { sky_blue } from "../../../const/variables";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  TAG_API,
  TAG_STATUS,
  TagObjectType,
  TblDmTagCategory,
} from "../../../model/TblDmTag";

type TagCategoryFormValues = {
  categoryName: string;
  type: string | null;
  description: string;
  allowMultiple: boolean;
  active: boolean;
};

const TagCategoryForm = ({
  category,
  objectTypes,
  onSaved,
}: TagCategoryFormProps) => {
  const [visible, { open, close }] = useDisclosure(false);

  const form = useForm<TagCategoryFormValues>({
    validateInputOnChange: true,
    initialValues: {
      categoryName: category?.categoryName ?? "",
      type: category?.type ?? null,
      description: category?.description ?? "",
      allowMultiple: category?.allowMultiple ?? true,
      active: category ? category.status !== TAG_STATUS.inactive : true,
    },
    validate: {
      categoryName: (value) => {
        if (!value?.trim()) return "Vui lòng nhập tên danh mục !";
        if (value.trim().length > 255)
          return "Tên danh mục không được dài hơn 255 ký tự !";
        return null;
      },
      type: (value) => (value ? null : "Vui lòng chọn đối tượng áp dụng !"),
    },
  });

  const handleSubmit = async (values: TagCategoryFormValues) => {
    open();
    const body = {
      id: category?.id,
      categoryName: values.categoryName.trim(),
      type: values.type,
      allowMultiple: values.allowMultiple,
      description: values.description?.trim(),
      status: values.active ? TAG_STATUS.active : TAG_STATUS.inactive,
    };
    const res = category
      ? await repositoryPos.put<MessageResponse<boolean>>(
          TAG_API.updateCategory,
          body
        )
      : await repositoryPos.post<MessageResponse<string>>(
          TAG_API.createCategory,
          body
        );
    close();
    if (res?.success) {
      NotificationExtension.Success(
        category ? "Cập nhật thành công !" : "Thêm thành công !"
      );
      onSaved();
      modals.closeAll();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={form.onSubmit(handleSubmit)}
      w={{ base: "300px", md: "440px" }}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Grid mt={10}>
        <Grid.Col span={12}>
          <TextInput
            label="Tên danh mục"
            placeholder="Nhập tên danh mục"
            withAsterisk
            {...form.getInputProps("categoryName")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Select
            label="Đối tượng áp dụng"
            placeholder="Chọn đối tượng áp dụng"
            withAsterisk
            data={objectTypes.map((o) => ({ value: o.code, label: o.name }))}
            {...form.getInputProps("type")}
          />
          <Box fz="xs" c="dark.4" mt={4}>
            Tag trong danh mục chỉ hiện ở màn của đối tượng này. Không đổi được
            sau khi đã có tag được gắn.
          </Box>
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label="Mô tả"
            autosize
            minRows={2}
            {...form.getInputProps("description")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Checkbox
            label="Cho phép gắn nhiều tag"
            description="Tắt: mỗi đối tượng chỉ chọn 1 tag trong danh mục"
            {...form.getInputProps("allowMultiple", { type: "checkbox" })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Checkbox
            label="Sử dụng"
            {...form.getInputProps("active", { type: "checkbox" })}
          />
        </Grid.Col>
      </Grid>

      <Group justify="end" mt="md">
        <Button
          type="button"
          color="gray"
          loading={visible}
          onClick={() => modals.closeAll()}
          leftSection={!visible ? <IconWindow size={18} /> : undefined}
        >
          Đóng
        </Button>
        <Button
          type="submit"
          color={sky_blue.base}
          loading={visible}
          leftSection={!visible ? <IconCheck size={18} /> : undefined}
        >
          Lưu
        </Button>
      </Group>
    </Box>
  );
};

export default TagCategoryForm;

type TagCategoryFormProps = {
  category?: TblDmTagCategory;
  objectTypes: TagObjectType[];
  onSaved: () => void;
};
